import { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth, API, apiClient } from "@/App";
import { useBranding } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { Bike, Car, Shield, Clock, IndianRupee, ChevronRight, User, Phone, Mail, Lock, Users, Smartphone, ArrowLeft, KeyRound } from "lucide-react";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

// Auto Rickshaw SVG Icon Component - Clean line art style
const AutoRickshawIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Roof/Canopy */}
    <path 
      d="M8 20C8 18 10 16 12 16H44C46 16 48 18 48 20V22H8V20Z" 
      stroke="currentColor" 
      strokeWidth="2.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
      fill="none"
    />
    {/* Body frame */}
    <path 
      d="M8 22V42C8 44 10 46 12 46H16M48 22V36M48 36C52 36 54 38 54 40V42M48 36V22" 
      stroke="currentColor" 
      strokeWidth="2.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
      fill="none"
    />
    {/* Window (left side) */}
    <rect x="12" y="24" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="2" fill="none"/>
    {/* Door opening */}
    <path d="M30 24V42" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M30 24H44V42H30" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    {/* Door handle */}
    <ellipse cx="40" cy="34" rx="2" ry="1.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
    {/* Mirror/handle at top */}
    <path d="M44 20C46 18 48 18 48 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    {/* Rear wheel (larger) */}
    <circle cx="18" cy="48" r="7" stroke="currentColor" strokeWidth="2.5" fill="none"/>
    <circle cx="18" cy="48" r="3" stroke="currentColor" strokeWidth="1.5" fill="none"/>
    {/* Front wheel (smaller) */}
    <circle cx="52" cy="48" r="5" stroke="currentColor" strokeWidth="2.5" fill="none"/>
    <circle cx="52" cy="48" r="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
    {/* Fender/mudguard */}
    <path d="M6 46H10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
    {/* Ground shadow line */}
    <path d="M10 56H56" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.4"/>
    {/* Handlebar */}
    <path d="M54 42V44C54 45 55 46 56 46" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const LandingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, login } = useAuth();
  const { appName, logoBase64 } = useBranding();
  const [showAuth, setShowAuth] = useState(false);
  const [authMethod, setAuthMethod] = useState("phone"); // email, phone, google - default to phone for customers
  const [authMode, setAuthMode] = useState("login");
  const [selectedRole, setSelectedRole] = useState("customer");
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
  });
  
  // Phone OTP state
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [processingOAuth, setProcessingOAuth] = useState(false);
  const [isExistingUser, setIsExistingUser] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  
  // Forgot Password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Rider App Store URLs
  const [riderAppUrls, setRiderAppUrls] = useState({
    playstore_url: "https://play.google.com/store/apps/details?id=com.bizilgo.rider",
    appstore_url: "https://apps.apple.com/app/bizilgo-rider"
  });
  const [isIOSDevice, setIsIOSDevice] = useState(false);

  // Detect iOS and fetch Rider app URLs
  useEffect(() => {
    // Detect iOS
    const userAgent = navigator.userAgent || navigator.vendor || window.opera || '';
    setIsIOSDevice(/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream);
    
    // Fetch app URLs from settings (playstore_url/appstore_url are Rider App URLs)
    const fetchRiderAppUrls = async () => {
      try {
        const res = await axios.get(`${API}/settings/app-urls`);
        if (res.data.playstore_url || res.data.appstore_url) {
          setRiderAppUrls({
            playstore_url: res.data.playstore_url || riderAppUrls.playstore_url,
            appstore_url: res.data.appstore_url || riderAppUrls.appstore_url
          });
        }
      } catch (error) {
        // Use defaults
      }
    };
    fetchRiderAppUrls();
  }, []);

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      const routes = { 
        customer: "/customer", 
        driver: "/customer",  // Drivers use customer dashboard on web to book rides
        admin: "/admin", 
        sub_admin: "/admin",  // Sub-admins use admin dashboard
        superadmin: "/superadmin", 
        sub_super_admin: "/superadmin",  // Sub-super-admins use superadmin dashboard
        city_admin: "/city-admin" 
      };
      navigate(routes[user.role] || "/");
    }
  }, [user, navigate]);

  // Resend OTP countdown timer
  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  // Check for reset token in URL
  useEffect(() => {
    const token = searchParams.get("reset_token");
    if (token) {
      setResetToken(token);
      setShowResetPassword(true);
    }
  }, [searchParams]);

  // Handle Google OAuth callback
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes("session_id=")) {
      setProcessingOAuth(true);
      const sessionId = hash.split("session_id=")[1]?.split("&")[0];
      if (sessionId) {
        // Get role from localStorage (set before redirect)
        const savedRole = localStorage.getItem("oauth_role") || "customer";
        handleGoogleCallback(sessionId, savedRole);
      }
    }
  }, []);

  // Auto-select superadmin role when email or phone matches
  useEffect(() => {
    const isSuperAdminEmail = formData.email.toLowerCase() === "tbharathk@gmail.com";
    const isSuperAdminPhone = formData.phone === "9858558555";
    const isSuperAdmin = isSuperAdminEmail || isSuperAdminPhone;
    
    if (isSuperAdmin && selectedRole !== "superadmin") {
      setSelectedRole("superadmin");
    } else if (!isSuperAdmin && selectedRole === "superadmin") {
      setSelectedRole("customer");
    }
  }, [formData.email, formData.phone, selectedRole]);

  const handleGoogleCallback = async (sessionId, role) => {
    try {
      const res = await axios.post(`${API}/auth/google/session`, {
        session_id: sessionId,
        role: role
      });
      
      login(res.data.token, res.data.user);
      localStorage.removeItem("oauth_role");
      
      // Clear hash from URL
      window.history.replaceState(null, "", window.location.pathname);
      
      toast.success(res.data.is_new_user ? "Account created successfully!" : "Welcome back!");
      const routes = { 
        customer: "/customer", 
        driver: "/customer",  // Drivers use customer dashboard on web to book rides
        admin: "/admin", 
        sub_admin: "/admin",
        superadmin: "/superadmin", 
        sub_super_admin: "/superadmin",
        city_admin: "/city-admin" 
      };
      const actualRole = res.data.user?.role || role;
      navigate(routes[actualRole] || routes[role] || "/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Google authentication failed");
      setProcessingOAuth(false);
      window.history.replaceState(null, "", window.location.pathname);
    }
  };

  // Show loading while processing OAuth or if user is logged in (redirect in progress)
  if (processingOAuth || user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">{user ? "Redirecting..." : "Signing you in..."}</p>
        </div>
      </div>
    );
  }

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = authMode === "login" ? "/auth/login" : "/auth/register";
      const payload = authMode === "login"
        ? { email: formData.email, password: formData.password, role: selectedRole }
        : { ...formData, role: selectedRole };

      const res = await axios.post(`${API}${endpoint}`, payload);
      login(res.data.token, res.data.user);
      toast.success(authMode === "login" ? "Welcome back!" : "Account created successfully!");
      
      const routes = { 
        customer: "/customer", 
        driver: "/customer",  // Drivers use customer dashboard on web to book rides
        admin: "/admin", 
        sub_admin: "/admin",
        superadmin: "/superadmin", 
        sub_super_admin: "/superadmin",
        city_admin: "/city-admin" 
      };
      const actualRole = res.data.user?.role || selectedRole;
      navigate(routes[actualRole] || routes[selectedRole] || "/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSendOTP = async (isResend = false) => {
    if (!formData.phone || formData.phone.length !== 10) {
      toast.error("Please enter a valid 10-digit phone number");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/otp/send`, {
        phone: formData.phone,
        role: selectedRole
      });
      setOtpSent(true);
      setResendTimer(30); // Start 30 second countdown
      
      // Auto-fill name if existing user
      if (res.data.existing_user && res.data.user_name) {
        setFormData(prev => ({ ...prev, name: res.data.user_name }));
        setIsExistingUser(true);
      } else {
        setIsExistingUser(false);
      }
      
      toast.success(isResend ? "OTP resent successfully!" : res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) {
      toast.error("Please enter the 6-digit OTP");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/otp/verify`, {
        phone: formData.phone,
        otp: otp,
        role: selectedRole,
        name: formData.name || null
      });
      
      if (res.data.is_new_user && !formData.name) {
        // New user needs to provide name
        toast.info("Please enter your name to complete registration");
        setLoading(false);
        return;
      }
      
      login(res.data.token, res.data.user);
      toast.success(res.data.is_new_user ? "Account created!" : "Welcome back!");
      
      // Redirect based on actual user role (not selected role - sub_admin selects "admin" but has role "sub_admin")
      const routes = { 
        customer: "/customer", 
        driver: "/customer",  // Drivers use customer dashboard on web to book rides
        admin: "/admin", 
        sub_admin: "/admin",
        superadmin: "/superadmin", 
        sub_super_admin: "/superadmin",
        city_admin: "/city-admin" 
      };
      const actualRole = res.data.user.role;
      navigate(routes[actualRole] || routes[selectedRole] || "/");
    } catch (err) {
      if (err.response?.data?.detail?.includes("Name is required")) {
        toast.info("Please enter your name to complete registration");
      } else {
        toast.error(err.response?.data?.detail || "OTP verification failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    // Save role to localStorage before redirect
    localStorage.setItem("oauth_role", selectedRole);
    
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotPasswordEmail) {
      toast.error("Please enter your email");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, {
        email: forgotPasswordEmail,
        role: selectedRole
      });
      
      toast.success(res.data.message);
      
      // Show the reset token (MOCK MODE)
      if (res.data.reset_token) {
        toast.info(`Reset Token: ${res.data.reset_token}`, { duration: 10000 });
        setResetToken(res.data.reset_token);
        setShowForgotPassword(false);
        setShowResetPassword(true);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send reset link");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/reset-password`, {
        token: resetToken,
        new_password: newPassword
      });
      
      toast.success(res.data.message);
      setShowResetPassword(false);
      setResetToken("");
      setNewPassword("");
      setConfirmPassword("");
      
      // Open login dialog
      setShowAuth(true);
      setAuthMode("login");
      
      // Clear URL params
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: Clock, title: "Quick Pickup", desc: "Get a ride in under 3 minutes" },
    { icon: IndianRupee, title: "Affordable Fares", desc: "Best prices in your city" },
    { icon: Shield, title: "Safe Rides", desc: "Verified drivers & tracking" },
  ];

  // Check if superadmin email/phone is entered
  const isSuperAdminEmail = formData.email.toLowerCase() === "tbharathk@gmail.com";
  const isSuperAdminPhone = formData.phone === "9858558555";
  const isSuperAdminUser = isSuperAdminEmail || isSuperAdminPhone;
  
  // Customer only in main auth - Admin/Rider via bottom links
  const roles = [
    { id: "customer", label: "Customer", icon: User, desc: "Book rides" },
    { id: "admin", label: "Admin", icon: Shield, desc: "Manage fleet" },
    ...(isSuperAdminUser ? [{ id: "superadmin", label: "Super Admin", icon: Shield, desc: "Platform owner" }] : []),
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Reset Password Modal */}
      <Dialog open={showResetPassword} onOpenChange={setShowResetPassword}>
        <DialogContent className="sm:max-w-md" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-primary" />
              Reset Your Password
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input
                type="password"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Confirm Password</Label>
              <Input
                type="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                data-testid="confirm-password-input"
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="reset-password-btn">
              {loading ? "Resetting..." : "Reset Password"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Forgot Password Modal */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="sm:max-w-md" data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle>Forgot Password</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground mb-4">
            Enter your email address and we'll send you a link to reset your password.
          </p>
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="email"
                  placeholder="you@example.com"
                  className="pl-10"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  required
                  data-testid="forgot-email-input"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowForgotPassword(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button type="submit" className="flex-1" disabled={loading} data-testid="send-reset-link-btn">
                {loading ? "Sending..." : "Send Reset Link"}
              </Button>
            </div>
          </form>
          <p className="text-xs text-muted-foreground text-center mt-2">
            Note: In demo mode, the reset token will be shown in a notification.
          </p>
        </DialogContent>
      </Dialog>

      {/* Hero Section */}
      <div className="relative h-[70vh] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1760782062730-6f431f5cfeaa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwzfHxpbmRpYW4lMjBjaXR5JTIwdHJhZmZpYyUyMG5pZ2h0JTIwbW90aW9uJTIwYmx1ciUyMHN0cmVldHxlbnwwfHx8fDE3NzI0NDk1OTJ8MA&ixlib=rb-4.1.0&q=85')`,
          }}
        />
        <div className="absolute inset-0 hero-overlay" />
        
        <div className="relative z-10 h-full flex flex-col">
          {/* Header */}
          <header className="flex items-center justify-between p-6">
            <div className="flex items-center gap-2">
              {logoBase64 ? (
                <img src={logoBase64} alt={appName} className="w-10 h-10 object-contain" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
                  <Bike className="w-5 h-5 text-white" />
                </div>
              )}
              <span className="text-2xl font-bold text-white tracking-tight">{appName}</span>
            </div>
            <Button
              variant="outline"
              className="bg-white/10 backdrop-blur-md border-white/20 text-white hover:bg-white/20"
              onClick={() => setShowAuth(true)}
              data-testid="header-login-btn"
            >
              Login
            </Button>
          </header>

          {/* Hero Content */}
          <div className="flex-1 flex flex-col justify-center px-6 max-w-lg">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight">
              Your Ride,<br />
              <span className="text-primary">Anytime</span>
            </h1>
            <p className="mt-4 text-lg text-white/80">
              Fast, affordable bike & auto rides at your fingertips. Get moving in minutes.
            </p>
            <Button
              size="lg"
              className="mt-8 w-fit h-14 px-8 rounded-full bg-primary hover:bg-primary/90 text-black font-semibold shadow-lg"
              onClick={() => setShowAuth(true)}
              data-testid="get-started-btn"
            >
              Get Started
              <ChevronRight className="ml-2 w-5 h-5" />
            </Button>
            
            {/* Join as Rider Button - Hero Section */}
            <a
              href={isIOSDevice && riderAppUrls.appstore_url ? riderAppUrls.appstore_url : riderAppUrls.playstore_url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full border-2 border-white/80 bg-white/10 backdrop-blur-sm text-white font-semibold hover:bg-white/20 transition-all"
              data-testid="hero-join-rider-btn"
            >
              <Bike className="w-5 h-5" />
              Join as a Rider & Earn
            </a>
          </div>
        </div>
      </div>

      {/* Vehicle Types */}
      <div className="px-6 py-12 bg-card">
        <h2 className="text-2xl font-bold text-foreground mb-6">Choose Your Ride</h2>
        <div className="grid grid-cols-3 gap-2 sm:gap-4">
          <div 
            className="p-3 sm:p-6 rounded-xl border border-border hover:border-secondary/50 transition-all cursor-pointer text-center" 
            data-testid="vehicle-car"
            onClick={() => setShowAuth(true)}
          >
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-100 flex items-center justify-center mb-2 sm:mb-4 mx-auto">
              <Car className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
            </div>
            <h3 className="font-semibold text-foreground text-sm sm:text-base">Car</h3>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1 line-clamp-2">Premium</p>
            <p className="text-sm sm:text-lg font-numbers font-semibold text-blue-600 mt-2">From ₹50</p>
          </div>
          <div 
            className="p-3 sm:p-6 rounded-xl border border-border hover:border-primary/50 transition-all cursor-pointer text-center" 
            data-testid="vehicle-auto"
            onClick={() => setShowAuth(true)}
          >
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-green-100 flex items-center justify-center mb-2 sm:mb-4 mx-auto">
              <AutoRickshawIcon className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
            </div>
            <h3 className="font-semibold text-foreground text-sm sm:text-base">Auto</h3>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1 line-clamp-2">Comfortable</p>
            <p className="text-sm sm:text-lg font-numbers font-semibold text-green-600 mt-2">From ₹30</p>
          </div>
          <div 
            className="p-3 sm:p-6 rounded-xl border border-border hover:border-primary/50 transition-all cursor-pointer text-center" 
            data-testid="vehicle-bike"
            onClick={() => setShowAuth(true)}
          >
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-orange-100 flex items-center justify-center mb-2 sm:mb-4 mx-auto">
              <Bike className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
            </div>
            <h3 className="font-semibold text-foreground text-sm sm:text-base">Bike</h3>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1 line-clamp-2">Quick rides</p>
            <p className="text-sm sm:text-lg font-numbers font-semibold text-orange-600 mt-2">From ₹20</p>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="px-6 py-12">
        <h2 className="text-2xl font-bold text-foreground mb-6">Why BizilGO?</h2>
        <div className="space-y-4">
          {features.map((f, i) => (
            <div key={i} className="flex items-center gap-4 p-4 rounded-xl bg-card border border-border/50">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <f.icon className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">{f.title}</h3>
                <p className="text-sm text-muted-foreground">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Safety Section */}
      <div className="px-6 py-12 bg-secondary text-white">
        <div className="flex items-center gap-4">
          <img
            src="https://images.unsplash.com/photo-1711533865848-829b5b322d73?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxtb3RvcmN5Y2xlJTIwaGVsbWV0JTIwY2xvc2UlMjB1cCUyMHNhZmV0eXxlbnwwfHx8fDE3NzI0NDk1OTR8MA&ixlib=rb-4.1.0&q=85"
            alt="Safety helmet"
            className="w-24 h-24 rounded-xl object-cover"
          />
          <div>
            <h3 className="text-xl font-bold">Safety First</h3>
            <p className="text-sm text-white/70 mt-1">
              All our drivers are verified and trained. Helmets provided on every ride.
            </p>
          </div>
        </div>
      </div>

      {/* CTA Footer */}
      <div className="px-6 py-12 text-center">
        <h2 className="text-2xl font-bold text-foreground">Ready to ride?</h2>
        <p className="text-muted-foreground mt-2">Join thousands of happy riders today</p>
        <Button
          size="lg"
          className="mt-6 h-14 px-10 rounded-full bg-primary hover:bg-primary/90 text-black font-semibold"
          onClick={() => setShowAuth(true)}
          data-testid="cta-book-ride-btn"
        >
          Book a Ride Now
        </Button>
        
        {/* Join as Rider & Admin Login Links */}
        <div className="mt-8 flex flex-col gap-4">
          <a 
            href={isIOSDevice && riderAppUrls.appstore_url ? riderAppUrls.appstore_url : riderAppUrls.playstore_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 h-14 px-8 rounded-full bg-accent hover:bg-accent/90 text-black font-semibold shadow-lg transition-all"
            data-testid="join-as-rider-link"
          >
            <Bike className="w-5 h-5" />
            Join as a Rider (Earn with us)
          </a>
          <div className="flex items-center justify-center gap-4 text-sm">
            <button
              onClick={() => { setShowAuth(true); setSelectedRole("admin"); setAuthMethod("phone"); }}
              className="text-muted-foreground hover:text-foreground text-xs"
              data-testid="admin-login-link"
            >
              Admin Login
            </button>
            <span className="text-muted-foreground">•</span>
            <button
              onClick={() => { setShowAuth(true); setSelectedRole("city_admin"); setAuthMethod("phone"); }}
              className="text-muted-foreground hover:text-foreground text-xs"
              data-testid="city-admin-login-link"
            >
              City Admin Login
            </button>
          </div>
        </div>
      </div>

      {/* Auth Dialog */}
      <Dialog open={showAuth} onOpenChange={setShowAuth}>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden max-h-[90vh] flex flex-col" data-testid="auth-dialog">
          <div className="bg-primary p-6 text-gray-900 flex-shrink-0">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-gray-900">
                {authMethod === "phone" && otpSent ? "Verify OTP" : 
                 authMode === "login" ? "Welcome Back" : "Create Account"}
              </DialogTitle>
            </DialogHeader>
            <p className="text-gray-700 mt-1">
              {authMethod === "phone" && otpSent ? "Enter the 6-digit code sent to your phone" :
               authMode === "login" ? "Sign in to continue" : "Join BizilGO today"}
            </p>
          </div>

          <div className="p-6 overflow-y-auto flex-1 pb-8">
            {/* Role Selection - Only show for Admin/SuperAdmin */}
            {selectedRole === "admin" || selectedRole === "superadmin" ? (
              <div className="mb-6">
                <Label className="text-sm text-muted-foreground mb-3 block">I am a</Label>
                <div className="grid grid-cols-2 gap-2">
                  {roles.filter(r => r.id === "admin" || r.id === "superadmin").map((role) => (
                    <button
                      key={role.id}
                      onClick={() => setSelectedRole(role.id)}
                      className={`p-3 rounded-xl border-2 transition-all text-center ${
                        selectedRole === role.id
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-primary/30"
                      }`}
                      data-testid={`role-${role.id}-btn`}
                    >
                      <role.icon className={`w-5 h-5 mx-auto ${selectedRole === role.id ? "text-primary" : "text-muted-foreground"}`} />
                      <span className={`text-sm font-medium mt-1 block ${selectedRole === role.id ? "text-primary" : "text-foreground"}`}>
                        {role.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {/* Auth Method Selection - Phone only for Admin and Customer */}
            {selectedRole === "superadmin" ? (
              <>
                <div className="flex gap-2 mb-4">
                  <Button
                    variant={authMethod === "email" ? "default" : "outline"}
                    className="flex-1 h-10"
                    onClick={() => { setAuthMethod("email"); setOtpSent(false); }}
                    data-testid="auth-method-email"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Email
                  </Button>
                  <Button
                    variant={authMethod === "phone" ? "default" : "outline"}
                    className="flex-1 h-10"
                    onClick={() => { setAuthMethod("phone"); setOtpSent(false); }}
                    data-testid="auth-method-phone"
                  >
                    <Smartphone className="w-4 h-4 mr-2" />
                    Phone
                  </Button>
                </div>
                
                {/* Google Sign In - SuperAdmin only */}
                <Button
                  variant="outline"
                  className="w-full h-12 mb-4 border-2"
                  onClick={handleGoogleSignIn}
                  data-testid="google-signin-btn"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </Button>

                <div className="relative mb-4">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">Or</span>
                  </div>
                </div>
              </>
            ) : null}

            {/* Email Authentication - SuperAdmin only */}
            {authMethod === "email" && selectedRole === "superadmin" && (
              <>
                <Tabs value={authMode} onValueChange={setAuthMode}>
                  <TabsList className="w-full mb-4">
                    <TabsTrigger value="login" className="flex-1" data-testid="login-tab">Login</TabsTrigger>
                    <TabsTrigger value="register" className="flex-1" data-testid="register-tab">Register</TabsTrigger>
                  </TabsList>
                </Tabs>

                <form onSubmit={handleEmailAuth}>
                  {authMode === "register" && (
                    <div className="space-y-4 mb-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name</Label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            id="name"
                            placeholder="John Doe"
                            className="pl-10 h-12"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            required={authMode === "register"}
                            data-testid="name-input"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone Number</Label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            id="phone"
                            placeholder="Enter phone number"
                            className="pl-10 h-12"
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                            required={authMode === "register"}
                            maxLength={10}
                            data-testid="phone-input"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="email"
                          type="email"
                          placeholder="you@example.com"
                          className="pl-10 h-12"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                          data-testid="email-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="password"
                          type="password"
                          placeholder="••••••••"
                          className="pl-10 h-12"
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          required
                          data-testid="password-input"
                        />
                      </div>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 mt-6 rounded-full font-semibold"
                    disabled={loading}
                    data-testid="auth-submit-btn"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : authMode === "login" ? (
                      "Sign In"
                    ) : (
                      "Create Account"
                    )}
                  </Button>
                  
                  {authMode === "login" && (
                    <button
                      type="button"
                      onClick={() => {
                        setForgotPasswordEmail(formData.email);
                        setShowForgotPassword(true);
                      }}
                      className="w-full text-center text-sm text-primary hover:underline mt-4"
                      data-testid="forgot-password-link"
                    >
                      Forgot Password?
                    </button>
                  )}
                </form>
              </>
            )}

            {/* Phone OTP Authentication */}
            {authMethod === "phone" && (
              <div className="space-y-6">
                {!otpSent ? (
                  <>
                    <div className="space-y-3">
                      <Label htmlFor="phone-otp">Phone Number</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="phone-otp"
                          placeholder="Enter phone number"
                          className="pl-10 h-14 text-lg"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                          maxLength={10}
                          inputMode="numeric"
                          data-testid="phone-otp-input"
                        />
                      </div>
                    </div>
                    <Button
                      className="w-full h-14 rounded-full font-semibold text-lg mt-4"
                      onClick={handleSendOTP}
                      disabled={loading}
                      data-testid="send-otp-btn"
                    >
                      {loading ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        "Send OTP"
                      )}
                    </Button>
                    <p className="text-xs text-center text-muted-foreground mt-4">
                      We'll send a 6-digit code to verify your number
                    </p>
                  </>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label>Enter OTP</Label>
                      <div 
                        className="flex justify-center"
                        onClick={() => {
                          // Scroll the OTP input into view when clicked on mobile
                          setTimeout(() => {
                            const otpElement = document.querySelector('[data-testid="otp-input"]');
                            if (otpElement) {
                              otpElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }
                          }, 300);
                        }}
                      >
                        <InputOTP
                          maxLength={6}
                          value={otp}
                          onChange={setOtp}
                          data-testid="otp-input"
                          autoFocus
                        >
                          <InputOTPGroup>
                            <InputOTPSlot index={0} />
                            <InputOTPSlot index={1} />
                            <InputOTPSlot index={2} />
                            <InputOTPSlot index={3} />
                            <InputOTPSlot index={4} />
                            <InputOTPSlot index={5} />
                          </InputOTPGroup>
                        </InputOTP>
                      </div>
                      <p className="text-xs text-center text-muted-foreground mt-2">
                        Enter the 6-digit OTP sent to your phone
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="otp-name">Your Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="otp-name"
                          placeholder="John Doe"
                          className={`pl-10 h-12 ${isExistingUser ? 'bg-muted cursor-not-allowed' : ''}`}
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          readOnly={isExistingUser}
                          data-testid="otp-name-input"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {isExistingUser ? "Welcome back! Your name is pre-filled." : "Required for new users"}
                      </p>
                    </div>

                    <Button
                      className="w-full h-12 rounded-full font-semibold"
                      onClick={handleVerifyOTP}
                      disabled={loading || otp.length !== 6}
                      data-testid="verify-otp-btn"
                    >
                      {loading ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        "Verify & Continue"
                      )}
                    </Button>

                    {/* Resend OTP Button */}
                    <div className="text-center">
                      {resendTimer > 0 ? (
                        <p className="text-sm text-muted-foreground">
                          Resend OTP in <span className="font-semibold text-primary">{resendTimer}s</span>
                        </p>
                      ) : (
                        <Button
                          variant="link"
                          className="text-primary p-0 h-auto"
                          onClick={() => handleSendOTP(true)}
                          disabled={loading}
                          data-testid="resend-otp-btn"
                        >
                          Didn't receive OTP? Resend
                        </Button>
                      )}
                    </div>

                    <Button
                      variant="ghost"
                      className="w-full"
                      onClick={() => { setOtpSent(false); setOtp(""); setIsExistingUser(false); setResendTimer(0); setFormData(prev => ({ ...prev, name: "" })); }}
                      data-testid="change-phone-btn"
                    >
                      Change phone number
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPage;
